import { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ExpandMore, ExpandLess, EditNote } from '@mui/icons-material';
import type { AtterbergGeneral, USCSGroupSymbol } from '../../../types/atterberg';

interface Props {
  data: AtterbergGeneral;
  onChange: (field: keyof AtterbergGeneral, value: number | string | null) => void;
  readOnly?: boolean;
}

const USCS_OPTIONS: USCSGroupSymbol[] = ['CL', 'CH', 'ML', 'MH', 'OL', 'OH', 'CL-ML'];

const inputSx = {
  '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
  '& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
};

export const OverridePanel = ({ data, onChange, readOnly = false }: Props) => {
  const theme = useTheme();
  const hasAnyOverride = data.overrideLL !== null
    || data.overridePL !== null
    || data.overridePI !== null
    || (data.overrideUSCSGroupSymbol !== null && data.overrideUSCSGroupSymbol !== '');
  const [expanded, setExpanded] = useState(hasAnyOverride);

  const numVal = (key: keyof AtterbergGeneral) => {
    const v = data[key];
    return v === null || v === undefined ? '' : String(v);
  };
  const handleNum = (key: keyof AtterbergGeneral, raw: string) =>
    onChange(key, raw === '' ? null : Number(raw));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, borderRadius: 2,
        border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
        backgroundColor: alpha(theme.palette.warning.light, hasAnyOverride ? 0.12 : 0.04),
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        onClick={() => setExpanded((p) => !p)}
      >
        <EditNote sx={{ fontSize: 18, color: 'warning.dark' }} />
        <Typography variant="overline" sx={{
          fontWeight: 700, color: 'warning.dark', fontSize: '0.7rem', letterSpacing: 0.6, flex: 1,
        }}>
          Overrides {hasAnyOverride && '— active'}
        </Typography>
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1, fontSize: '0.7rem' }}>
          When set, override values replace the computed value everywhere — plots, USCS classification, PDF report. Leave blank to use computed.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          <TextField
            label="Override LL" type="number" size="small"
            value={numVal('overrideLL')}
            onChange={(e) => handleNum('overrideLL', e.target.value)}
            sx={inputSx}
            disabled={readOnly}
          />
          <TextField
            label="Override PL" type="number" size="small"
            value={numVal('overridePL')}
            onChange={(e) => handleNum('overridePL', e.target.value)}
            sx={inputSx}
            disabled={readOnly}
          />
          <TextField
            label="Override PI" type="number" size="small"
            value={numVal('overridePI')}
            onChange={(e) => handleNum('overridePI', e.target.value)}
            sx={inputSx}
            disabled={readOnly}
          />
          <TextField
            label="Override USCS" select size="small"
            value={data.overrideUSCSGroupSymbol ?? ''}
            onChange={(e) => onChange('overrideUSCSGroupSymbol', e.target.value || null)}
            sx={inputSx}
            disabled={readOnly}
          >
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>—</em></MenuItem>
            {USCS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Collapse>
    </Paper>
  );
};
